import { useCallback, useEffect, useState } from 'react';
import { getDailyAuditReportExpanded, type DailyExpandedAuditReport } from '../../services/storeAuditReportsService';

type UseDailyExpandedAuditReportParams = {
  enabled: boolean;
  storeId: string | null | undefined;
  reportDate: string | null | undefined;
};

type UseDailyExpandedAuditReportState = {
  data: DailyExpandedAuditReport | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

export const useDailyExpandedAuditReport = ({
  enabled,
  storeId,
  reportDate,
}: UseDailyExpandedAuditReportParams): UseDailyExpandedAuditReportState => {
  const [data, setData] = useState<DailyExpandedAuditReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled || !storeId || !reportDate) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getDailyAuditReportExpanded(storeId, reportDate);
      setData(response);
    } catch (e: any) {
      const message = e?.message ? String(e.message) : 'Failed to load expanded audit report';
      setError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [enabled, storeId, reportDate]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    data,
    loading,
    error,
    refresh,
  };
};

